import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { saasApi } from "../api/saasApi";
import { validateRegistrationForm, mapApiErrors, PASSWORD_HINT } from "../utils/validation";
import { persistAdminSession } from "../utils/session";
import "../styles/saas.css";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token");

  const [step, setStep] = useState(tokenFromUrl ? "verify" : "form");
  const [form, setForm] = useState({
    plan_slug: searchParams.get("plan") || "starter",
    company_name: "",
    email: "",
    mobile: "",
    password: "",
    owner_name: "",
    gst_number: "",
    address: "",
    industry: "",
  });
  const [verify, setVerify] = useState({ otp: "" });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tokenFromUrl) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await saasApi.verifyEmail({ token: tokenFromUrl });
        if (cancelled) return;
        persistAdminSession(res.data);
        window.location.href = "/dashboard";
      } catch (err) {
        if (cancelled) return;
        setMessage(
          err.data?.message ||
            "This verification link expired. Enter the 6-digit code from your email, or resend a new code."
        );
        setMessageType("error");
        setStep("verify");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tokenFromUrl]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submitRegister = async (e) => {
    e.preventDefault();
    setMessage("");
    const v = validateRegistrationForm(form);
    setErrors(v);
    if (Object.keys(v).length) return;

    setLoading(true);
    try {
      const res = await saasApi.register({
        ...form,
        plan_slug: form.plan_slug || searchParams.get("plan") || "starter",
      });
      setMessage(res.message || "Verification code sent. Check your inbox and spam folder.");
      setMessageType("success");
      setStep("verify");
      setErrors({});
    } catch (err) {
      setMessageType("error");
      const apiErrors = mapApiErrors(err.data?.errors);
      if (Object.keys(apiErrors).length) {
        setErrors(apiErrors);
        setMessage("Please fix the highlighted fields.");
      } else {
        setMessage(err.data?.message || err.message || "Registration failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setMessage("");
    const otpDigits = verify.otp.replace(/\D/g, "");
    if (!tokenFromUrl && otpDigits.length !== 6) {
      setMessageType("error");
      setMessage("Enter the 6-digit code from your email.");
      return;
    }

    setLoading(true);
    try {
      const res = await saasApi.verifyEmail({
        token: tokenFromUrl || undefined,
        otp: otpDigits || undefined,
      });
      persistAdminSession(res.data);
      window.location.href = "/dashboard";
    } catch (err) {
      setMessageType("error");
      setMessage(err.data?.message || err.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (!form.email?.trim()) {
      setMessageType("error");
      setMessage("Go back and complete the signup form first.");
      setStep("form");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const res = form.password
        ? await saasApi.resendVerification(form)
        : await saasApi.resendVerificationEmail(form.email);
      setMessage(res.message || "A new verification code has been sent.");
      setMessageType("success");
    } catch (err) {
      setMessageType("error");
      setMessage(err.data?.message || err.message || "Could not resend code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="saas-page saas-auth">
      <Link to="/">← Back</Link>
      <h1>{step === "form" ? "Start your free trial" : "Verify your email"}</h1>
      {message && (
        <p className={messageType === "error" ? "saas-error" : messageType === "success" ? "saas-success" : "saas-message"}>
          {message}
        </p>
      )}

      {step === "form" ? (
        <form onSubmit={submitRegister} className="saas-form">
          <label>
            Company name *
            <input name="company_name" value={form.company_name} onChange={onChange} />
            {errors.company_name && <span className="saas-error">{errors.company_name}</span>}
          </label>
          <label>
            Email *
            <input name="email" type="email" value={form.email} onChange={onChange} />
            {errors.email && <span className="saas-error">{errors.email}</span>}
          </label>
          <label>
            Mobile *
            <input name="mobile" value={form.mobile} onChange={onChange} placeholder="10-digit number" />
            {errors.mobile && <span className="saas-error">{errors.mobile}</span>}
          </label>
          <label>
            Password *
            <input name="password" type="password" value={form.password} onChange={onChange} autoComplete="new-password" />
            <span className="saas-hint">{PASSWORD_HINT}</span>
            {errors.password && <span className="saas-error">{errors.password}</span>}
          </label>
          <label>Owner name<input name="owner_name" value={form.owner_name} onChange={onChange} /></label>
          <label>GST number<input name="gst_number" value={form.gst_number} onChange={onChange} /></label>
          <label>Address<textarea name="address" value={form.address} onChange={onChange} /></label>
          <label>Industry<input name="industry" value={form.industry} onChange={onChange} /></label>
          <button type="submit" className="saas-btn saas-btn--primary" disabled={loading}>
            {loading ? "Sending…" : "Continue"}
          </button>
        </form>
      ) : (
        <form onSubmit={submitVerify} className="saas-form">
          <p className="saas-hint">We sent a 6-digit code to <strong>{form.email || "your email"}</strong>. Check spam/promotions if you do not see it.</p>
          <label>
            Verification code
            <input
              value={verify.otp}
              onChange={(e) => setVerify({ otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
            />
          </label>
          <button type="submit" className="saas-btn saas-btn--primary" disabled={loading}>
            {loading ? "Verifying…" : "Create workspace"}
          </button>
          <button type="button" className="saas-btn saas-btn--secondary" onClick={resendCode} disabled={loading} style={{ marginTop: "0.75rem" }}>
            Resend code
          </button>
          <button type="button" className="saas-link-btn" onClick={() => setStep("form")}>
            ← Edit registration details
          </button>
        </form>
      )}
      <p>Already have an account? <Link to="/login">Log in</Link></p>
    </div>
  );
}
